import { useState, useEffect, useCallback, Fragment } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { useHeaderExtra } from '@/contexts/header.context'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { adminPartnersClient } from '@/lib/api'
import type {
  PartnerAuditLogEntry,
  PartnerAuditLogPage,
} from '@/lib/api/admin-partners.client'

const PAGE_SIZE = 25

const ACTION_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All actions' },
  { value: 'INVOCATION_RECEIVED', label: 'Invocation received' },
  { value: 'INVOCATION_SENT', label: 'Invocation sent' },
  { value: 'SEQUENCE_STOPPED', label: 'Sequence stopped' },
  { value: 'CREDENTIALS_GENERATED', label: 'Credentials generated' },
  { value: 'COMPANY_PROVISIONED', label: 'Company provisioned' },
  { value: 'STATUS_CHANGED', label: 'Status changed' },
]

const ACTION_LABELS: Record<string, string> = {
  INVOCATION_RECEIVED: 'Invocation received',
  INVOCATION_SENT: 'Invocation sent',
  SEQUENCE_STOPPED: 'Sequence stopped',
  CREDENTIALS_GENERATED: 'Credentials generated',
  TOKEN_EXCHANGE: 'Token exchange',
  COMPANY_PROVISIONED: 'Company provisioned',
  STATUS_CHANGED: 'Status changed',
}

const formatTimestamp = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

const eventNameOf = (log: PartnerAuditLogEntry): string | undefined => {
  const raw = log.newState?.eventName
  return typeof raw === 'string' ? raw : undefined
}

export function PartnerActivityPage() {
  const { id: partnerId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const actionFilter = searchParams.get('action') ?? 'all'
  const offset = Number(searchParams.get('offset') ?? '0')

  const [page, setPage] = useState<PartnerAuditLogPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!partnerId) return
    try {
      setLoading(true)
      const result = await adminPartnersClient.getPartnerAudit(partnerId, {
        action: actionFilter !== 'all' ? actionFilter : undefined,
        limit: PAGE_SIZE,
        offset,
      })
      setPage(result)
    } catch {
      toast.error('Failed to load partner activity')
      setPage(null)
    } finally {
      setLoading(false)
    }
  }, [partnerId, actionFilter, offset])

  useEffect(() => {
    load()
  }, [load])

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(updates)) {
      if (value == null) next.delete(key)
      else next.set(key, value)
    }
    setSearchParams(next)
  }

  const handleActionChange = (value: string) => {
    updateParams({ action: value === 'all' ? null : value, offset: null })
    setExpandedId(null)
  }

  const goToPage = (nextPage: number) => {
    const newOffset = Math.max(0, (nextPage - 1) * PAGE_SIZE)
    updateParams({ offset: newOffset === 0 ? null : String(newOffset) })
    setExpandedId(null)
  }

  const totalPages = page ? Math.max(1, Math.ceil(page.total / PAGE_SIZE)) : 1
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const startIndex = page ? offset : 0
  const endIndex = page ? Math.min(offset + page.logs.length, page.total) : 0

  useHeaderExtra(
    <Link
      to={ROUTE_REGISTRY.PARTNER_PARTNERS.path}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
    >
      <ArrowLeft className="w-4 h-4 text-muted-foreground" />
    </Link>,
    [],
  )

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="flex items-center gap-4 mt-6">
            <Select value={actionFilter} onValueChange={handleActionChange}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_FILTERS.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {page && (
              <span className="text-sm text-muted-foreground">
                {page.total} total
              </span>
            )}
          </div>

          <div className="rounded-lg border overflow-hidden min-h-96">
            {loading || page === null ? (
              <div className="p-6 space-y-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : page.logs.length === 0 ? (
              <EmptyState hasFilter={actionFilter !== 'all'} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider w-52">
                      Timestamp
                    </TableHead>
                    <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Action
                    </TableHead>
                    <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Bus event
                    </TableHead>
                    <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider w-32">
                      Resource
                    </TableHead>
                    <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {page.logs.map(log => (
                    <Fragment key={log.id}>
                      <TableRow
                        onClick={() =>
                          setExpandedId(prev =>
                            prev === log.id ? null : log.id,
                          )
                        }
                        className="cursor-pointer"
                      >
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap font-mono text-xs">
                          {formatTimestamp(log.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {ACTION_LABELS[log.action] ?? log.action}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-mono text-xs">
                          {eventNameOf(log) ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          <Badge variant="outline">{log.resourceType}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <Badge
                            variant={log.success ? 'default' : 'destructive'}
                          >
                            {log.success ? 'OK' : 'ERROR'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                      {expandedId === log.id && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30">
                            <ExpandedDetail log={log} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <FloatingActionIslandContent
          show={page !== null && totalPages > 1}
          componentId="partner-activity-pagination"
          priority={10}
        >
          <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
            <div className="flex-grow items-center space-x-2 text-sm text-muted-foreground whitespace-nowrap pointer-events-none hidden md:flex">
              <span className="font-medium">
                <span className="mr-1">{page?.total ?? 0} events</span>
                <span className="text-muted-foreground/60 mx-1">•</span>
                <span className="ml-1">
                  {startIndex + 1}-{endIndex} of {page?.total ?? 0}
                </span>
              </span>
            </div>

            <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
              <div className="inline-flex items-center space-x-1">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="inline-flex items-center space-x-1 justify-end">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNumber
                    if (totalPages <= 5) {
                      pageNumber = i + 1
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i
                    } else {
                      pageNumber = currentPage - 2 + i
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => goToPage(pageNumber)}
                        className={cn(
                          'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                          pageNumber === currentPage
                            ? 'z-10 bg-black/90 text-white border-black'
                            : 'bg-background/80 border-border text-muted-foreground hover:bg-muted hover:border-border',
                        )}
                      >
                        {pageNumber}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </FloatingActionIslandContent>

        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <Badge variant="outline" className="mb-4">
        No activity
      </Badge>
      <p className="max-w-sm text-sm text-muted-foreground">
        {hasFilter
          ? 'No activity matches the current filter.'
          : 'This partner has no logged activity yet.'}
      </p>
    </div>
  )
}

function ExpandedDetail({ log }: { log: PartnerAuditLogEntry }) {
  return (
    <div className="py-3 space-y-2">
      {log.resourceId && (
        <DetailRow label="Resource ID" value={log.resourceId} mono />
      )}
      {log.previousState && (
        <DetailRow
          label="Previous state"
          value={JSON.stringify(log.previousState, null, 2)}
          mono
          multiline
        />
      )}
      {log.newState && (
        <DetailRow
          label="New state"
          value={JSON.stringify(log.newState, null, 2)}
          mono
          multiline
        />
      )}
      {log.errorMessage && <DetailRow label="Error" value={log.errorMessage} />}
    </div>
  )
}

function DetailRow({
  label,
  value,
  mono,
  multiline,
}: {
  label: string
  value: string
  mono?: boolean
  multiline?: boolean
}) {
  return (
    <div className="flex gap-3">
      <span className="w-32 text-xs text-muted-foreground shrink-0 pt-0.5">
        {label}
      </span>
      {multiline ? (
        <pre
          className={cn(
            'text-xs bg-background rounded px-3 py-2 flex-1 overflow-x-auto',
            mono && 'font-mono',
          )}
        >
          {value}
        </pre>
      ) : (
        <span className={cn('text-sm break-all', mono && 'font-mono')}>
          {value}
        </span>
      )}
    </div>
  )
}
